/**
 * Parses a D&D Beyond character sheet PDF (fillable form) into a
 * structure matching the backend ImportedSheet shape.
 *
 * DDB PDFs are fillable AcroForms — all character data lives in named
 * form field annotations, not in the text stream.  We use pdfjs-dist's
 * getAnnotations() to read them, so no server-side tooling is needed.
 */

import * as pdfjsLib from 'pdfjs-dist';

// Point at the bundled worker.
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.mjs',
  import.meta.url,
).toString();

// ────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────

/** Mirrors the backend ImportedSheet. */
export interface ParsedSheet {
  className: string;
  subclass?: string;
  level: number;
  hitDieTotal: number;
  baseStats: Record<string, number>;
  asiDeltas: Record<string, number>;
  featDeltas: Record<string, number>;
  proficiencies: Record<string, string>;
  feats: string[];
  warnings: string[];
}

// ────────────────────────────────────────────────────────────────────
// Public API
// ────────────────────────────────────────────────────────────────────

/**
 * Parse a DDB character sheet PDF (as an ArrayBuffer from a file input)
 * into a ParsedSheet suitable for the "parsed" platform import endpoint.
 */
export async function parseDdbPdf(buffer: ArrayBuffer): Promise<ParsedSheet> {
  const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;

  // Collect all form field annotations across all pages into a flat map.
  const fields: Record<string, string> = {};
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const annotations = await page.getAnnotations();
    for (const a of annotations) {
      if (a.fieldName && a.fieldValue != null && a.fieldValue !== '') {
        fields[a.fieldName.trim()] = String(a.fieldValue);
      }
    }
  }

  const warnings: string[] = [];

  // ── Class & Level ──
  const classLevelRaw = fields['CLASS  LEVEL'] ?? fields['CLASS LEVEL'] ?? '';
  const classLevel = parseClassLevel(classLevelRaw);
  if (!classLevel) {
    throw new Error(
      `Could not parse class & level from "${classLevelRaw}". Is this a D&D Beyond character sheet?`,
    );
  }

  // ── Ability scores ──
  const baseStats: Record<string, number> = {};
  for (const key of ['STR', 'DEX', 'CON', 'INT', 'WIS', 'CHA'] as const) {
    const raw = fields[key];
    if (raw) {
      const val = parseInt(raw, 10);
      if (!isNaN(val) && val >= 1 && val <= 30) baseStats[key] = val;
    }
  }
  if (Object.keys(baseStats).length < 6) {
    warnings.push(
      `Only found ${Object.keys(baseStats).length}/6 ability scores — check base stats after import`,
    );
  }

  // ── Max HP + Hit Dice ──
  const maxHp = parseInt(fields['MaxHP'] ?? '', 10) || null;
  const hitDice = parseHitDice(fields['Total'] ?? '');
  const conMod = Math.floor(((baseStats.CON ?? 10) - 10) / 2);

  let hitDieTotal: number;
  if (maxHp != null) {
    // hitDieTotal = maxHp - level * conMod (the backend formula)
    hitDieTotal = maxHp - classLevel.level * conMod;
  } else if (hitDice) {
    hitDieTotal = hitDice.count * hitDice.die; // rough estimate
    warnings.push('MaxHP field missing — hitDieTotal is estimated from hit dice');
  } else {
    const classDie = CLASS_HIT_DICE[classLevel.className.toLowerCase()] ?? 8;
    hitDieTotal = classDie * classLevel.level;
    warnings.push('MaxHP and hit dice fields missing — hitDieTotal is a rough estimate');
  }

  // Clamp to valid range.
  const classDie = hitDice?.die ?? CLASS_HIT_DICE[classLevel.className.toLowerCase()] ?? 8;
  hitDieTotal = Math.max(classLevel.level, Math.min(classLevel.level * classDie, hitDieTotal));

  // ── Saving throw proficiencies ──
  const saveProfKeys: [string, string][] = [
    ['StrProf', 'strength'],
    ['DexProf', 'dexterity'],
    ['ConProf', 'constitution'],
    ['IntProf', 'intelligence'],
    ['WisProf', 'wisdom'],
    ['ChaProf', 'charisma'],
  ];
  const proficiencies: Record<string, string> = {};
  for (const [fieldName, ability] of saveProfKeys) {
    if (fields[fieldName] === '•' || fields[fieldName] === 'P') {
      proficiencies[`saving-throw-${ability}`] = 'proficiency';
    }
  }

  // ── Skill proficiencies ──
  const skillProfKeys: [string, string][] = [
    ['AcrobaticsProf', 'acrobatics'],
    ['AnimalProf', 'animal-handling'],
    ['ArcanaProf', 'arcana'],
    ['AthleticsProf', 'athletics'],
    ['DeceptionProf', 'deception'],
    ['HistoryProf', 'history'],
    ['InsightProf', 'insight'],
    ['IntimidationProf', 'intimidation'],
    ['InvestigationProf', 'investigation'],
    ['MedicineProf', 'medicine'],
    ['NatureProf', 'nature'],
    ['PerceptionProf', 'perception'],
    ['PerformanceProf', 'performance'],
    ['PersuasionProf', 'persuasion'],
    ['ReligionProf', 'religion'],
    ['SleightofHandProf', 'sleight-of-hand'],
    ['StealthProf', 'stealth'],
    ['SurvivalProf', 'survival'],
  ];
  for (const [fieldName, skill] of skillProfKeys) {
    if (fields[fieldName] === 'P' || fields[fieldName] === '•') {
      proficiencies[`skill-${skill}`] = 'proficiency';
    }
  }

  // ── Feats (from FeaturesTraits fields) ──
  const feats = parseFeats(fields);

  // ── Subclass (from FeaturesTraits fields) ──
  const subclass = parseSubclass(fields);

  return {
    className: classLevel.className,
    subclass: subclass ?? undefined,
    level: classLevel.level,
    hitDieTotal,
    baseStats,
    asiDeltas: {}, // Can't distinguish base from ASI in a PDF export.
    featDeltas: {}, // Ditto.
    proficiencies,
    feats,
    warnings,
  };
}

// ────────────────────────────────────────────────────────────────────
// Internal helpers
// ────────────────────────────────────────────────────────────────────

function parseClassLevel(
  raw: string,
): { className: string; level: number } | null {
  // "Wizard 5", "Fighter 3 / Rogue 2", etc.
  const classPattern =
    /\b(Artificer|Barbarian|Bard|Blood\s*Hunter|Cleric|Druid|Fighter|Monk|Paladin|Ranger|Rogue|Sorcerer|Warlock|Wizard)\s+(\d{1,2})\b/i;
  const m = raw.match(classPattern);
  if (m) {
    return {
      className: m[1].charAt(0).toUpperCase() + m[1].slice(1).toLowerCase(),
      level: parseInt(m[2], 10),
    };
  }
  return null;
}

function parseHitDice(raw: string): { count: number; die: number } | null {
  // "5d6", "3d10", etc.
  const m = raw.match(/(\d+)d(\d+)/i);
  if (m) return { count: parseInt(m[1], 10), die: parseInt(m[2], 10) };
  return null;
}

/** Extract feat names from the FeaturesTraits form fields. */
function parseFeats(fields: Record<string, string>): string[] {
  const feats: string[] = [];
  // Combine all FeaturesTraits fields.
  const traitText = [
    fields['FeaturesTraits1'],
    fields['FeaturesTraits2'],
    fields['FeaturesTraits3'],
  ]
    .filter(Boolean)
    .join('\n');

  // Look for the "=== FEATS ===" section.
  const featsSection = traitText.match(
    /===\s*FEATS\s*===\s*\n([\s\S]*?)(?:===|$)/i,
  );
  if (featsSection) {
    const lines = featsSection[1].split('\n');
    for (const line of lines) {
      // Feat entries: "* Feat Name • Source"
      const m = line.match(/\*\s+(.+?)(?:\s*•\s*.+)?$/);
      if (m) {
        const name = m[1].trim();
        if (name && !name.startsWith('|') && name.length > 2) {
          feats.push(name);
        }
      }
    }
  }
  return feats;
}

/** Try to extract the subclass from the FeaturesTraits fields. */
function parseSubclass(fields: Record<string, string>): string | null {
  const traitText = [
    fields['FeaturesTraits1'],
    fields['FeaturesTraits2'],
    fields['FeaturesTraits3'],
  ]
    .filter(Boolean)
    .join('\n');

  // Common DDB patterns: "| School of Necromancy", "| Path of the Berserker", etc.
  const patterns = [
    /\|\s*(School\s+of\s+\w+)/i,
    /\|\s*(College\s+of\s+\w+)/i,
    /\|\s*(Path\s+of\s+(?:the\s+)?\w+)/i,
    /\|\s*(Circle\s+of\s+(?:the\s+)?\w+)/i,
    /\|\s*(Oath\s+of\s+(?:the\s+)?\w+)/i,
    /\|\s*(\w[\w\s]+?Domain)/i,
    /\|\s*(Way\s+of\s+(?:the\s+)?\w+)/i,
    /\|\s*(Order\s+of\s+(?:the\s+)?\w+)/i,
    /\|\s*(The\s+\w+(?:\s+Patron)?)/i,
  ];
  for (const p of patterns) {
    const m = traitText.match(p);
    if (m) return m[1].trim();
  }
  return null;
}

const CLASS_HIT_DICE: Record<string, number> = {
  barbarian: 12,
  fighter: 10,
  paladin: 10,
  ranger: 10,
  bard: 8,
  cleric: 8,
  druid: 8,
  monk: 8,
  rogue: 8,
  warlock: 8,
  'blood hunter': 10,
  artificer: 8,
  sorcerer: 6,
  wizard: 6,
};
